import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_users_email_continuity'
const CAMPUS_URL = 'https://campus.barracuda.com/doc/96023041/'

function UsersEmailContinuityI18n({
  classes,
  productName
}: {
  classes: ReturnType<typeof styles>
  productName?: string
}) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  return (
    <div className={classes.content} data-testid="content">
      <p>
        {formatMessage('intro_prefix')}
        <a href={CAMPUS_URL} target="_blank" rel="noreferrer">
          {formatMessage('intro_link')}
        </a>
      </p>
      <br />
      <br />
      {formatMessage('ldap_warning', { productName })}
      <br />
      <br />
      <ul>
        <li>
          <b>{formatMessage('email_continuity_label')}</b>
          <br />
          {formatMessage('email_continuity_description')}
          <br />
        </li>
        <ul>
          <li>
            <b>{formatMessage('settings.off.label')}</b>
            <br />
            {formatMessage('settings.off.description', { productName })}
          </li>
          <li>
            <b>{formatMessage('settings.auto_enable.label')}</b>
            <br />
            {formatMessage('settings.auto_enable.description', { productName })}
          </li>
        </ul>
      </ul>
      <h3>{formatMessage('user_actions.title')}</h3>
      {formatMessage('user_actions.intro')}
      <ul>
        <li>{formatMessage('user_actions.new_message')}</li>
        <li>{formatMessage('user_actions.reply')}</li>
        <li>{formatMessage('user_actions.forward')}</li>
        <li>{formatMessage('user_actions.download')}</li>
      </ul>
    </div>
  )
}

function UsersEmailContinuity(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context
  }

  if (isSmartlingEnabled) {
    return <UsersEmailContinuityI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <p>
        Email Continuity allows end-users to send, receive, compose, and forward emails when designated mail servers are
        unavailable. Note that Email Continuity is automatically disabled after 96 hours. Messages in Email Continuity
        are viewable in the Message list for 30 days, after which they expire. Enable spooling on the{' '}
        <b>Domains &gt; Domain Settings</b>
        page for each domain where you want to enable Email Continuity. For more information, see
      </p>
      <a href="https://campus.barracuda.com/doc/96023041/" target="_blank" rel="noreferrer">
        {' '}
        Barracuda Campus.
      </a>
      <br />
      <br />
      <b>Important</b>: If your organization is using LDAP Authentication and your LDAP server goes offline,{' '}
      {props.productName} cannot connect to your LDAP server, and therefore users are unable to log into Email
      Continuity.
      <br />
      <br />
      <ul>
        <li>
          <b>Email Continuity</b>
          <br />
          Specify whether to allow end-users to send, receive, compose, and forward emails when designated mail servers
          are unavailable.
          <br />
        </li>
        <ul>
          <li>
            <b>Off</b>
            <br />
            Email Continuity is disabled. If {props.productName} cannot connect to your mail server, the mail is
            deferred and the sending mail server is notified. Depending on its configuration, the sending mail server
            can retry the message or notify the sender that the mail was deferred or failed.
          </li>
          <li>
            <b>Auto-Enable</b>
            <br />
            When selected, Email Continuity automatically starts once it detects that {props.productName} has been
            spooling messages for a 10 minute period. Email Continuity automatically stops when it detects that{' '}
            {props.productName} has stopped spooling or when 96 hours elapses, whichever comes first.
          </li>
        </ul>
      </ul>
      <h3>User Actions</h3>
      Once Email Continuity starts, users can take the following actions:
      <ul>
        <li>
          Compose a <b>New Message</b>
        </li>
        <li>
          <b>Reply</b> to the sender
        </li>
        <li>
          <b>Forward</b> a message to one or more email addresses; separate multiple addresses with a comma delimiter
        </li>
        <li>
          <b>Download</b> messages from Email Continuity as .eml files
        </li>
      </ul>
    </div>
  )
}

export default UsersEmailContinuity
